import type { useRealtimeKitClient } from "@cloudflare/realtimekit-react";
import { createContext } from "react";

import type {
  LocalCallPhase,
  TableCallParticipantDirectory,
} from "./table-call-participant-directory.js";

export type TableCallStatus =
  | "disconnected"
  | "joining"
  | "leaving"
  | "connected";

export type TableCallAudioReadiness =
  | "disconnected"
  | "joining"
  | "applying-devices"
  | "connected"
  | "recovering";
export type MeetingClient = NonNullable<
  ReturnType<typeof useRealtimeKitClient>[0]
>;
export type InitClientFunction = ReturnType<typeof useRealtimeKitClient>[1];

export interface TableCallContextType {
  __ctx_init: boolean;
  status: TableCallStatus;
  sessionPhase: LocalCallPhase;
  audioReadiness: TableCallAudioReadiness;
  audioIssueMessage: string | null;
  meeting: MeetingClient | null;
  participantDirectory: TableCallParticipantDirectory | null;

  // Microphone
  canControlMicrophone: boolean;
  canEnableMicrophone: boolean;
  microphoneActive: boolean;
  setMicrophoneActive: (status: boolean) => Promise<void>;
  startPushToTalk: () => Promise<void>;
  stopPushToTalk: () => Promise<void>;
  setPreferredMicrophone: (deviceId: string) => Promise<void>;
  setPreferredSpeaker: (deviceId: string) => Promise<void>;
  /** True when user has toggled always-on mode (mic stays on, PTT is suppressed) */
  microphoneAlwaysOn: boolean;
  setMicrophoneAlwaysOn: (enabled: boolean) => Promise<void>;
}

export const TableCallContext = createContext<TableCallContextType>({
  __ctx_init: false,
  audioIssueMessage: null,
  audioReadiness: "disconnected",
  canControlMicrophone: false,
  canEnableMicrophone: false,
  meeting: null,
  microphoneActive: false,
  microphoneAlwaysOn: false,
  participantDirectory: null,
  sessionPhase: "idle",
  setMicrophoneActive: () => Promise.resolve(),
  // Lint exception: Default noop for context initializer
  setMicrophoneAlwaysOn: () => Promise.resolve(),
  setPreferredMicrophone: () => Promise.resolve(),
  setPreferredSpeaker: () => Promise.resolve(),
  startPushToTalk: () => Promise.resolve(),
  status: "disconnected",
  stopPushToTalk: () => Promise.resolve(),
});
